import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  LocalShippingOutlined,
  SearchOutlined,
  VisibilityOutlined,
  EditOutlined,
  PersonAddOutlined,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { authService } from '../../services/authService';
import { ROLES } from '../../constants/roles';
import { Partner } from '../../types/api';

const SupplierList: React.FC = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<Partner[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Partner[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const currentUser = authService.getUser();
  const isAdmin = currentUser?.role === ROLES.ADMIN;

  useEffect(() => {
    fetchSuppliers();
  }, []);

  useEffect(() => {
    filterSuppliers();
  }, [searchQuery, suppliers]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/api/partners', { params: { type: 'SUPPLIER' } });
      const supplierData = response.data.partners || [];
      setSuppliers(Array.isArray(supplierData) ? supplierData : []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterSuppliers = () => {
    if (!Array.isArray(suppliers)) {
      setFilteredSuppliers([]);
      return;
    }
    if (!searchQuery.trim()) {
      setFilteredSuppliers(suppliers);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = suppliers.filter((supplier) => {
      return supplier.name.toLowerCase().includes(query);
    });
    setFilteredSuppliers(filtered);
  };

  return (
    <Paper sx={{ p: 2, m: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocalShippingOutlined sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            All Suppliers
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddOutlined />}
          onClick={() => navigate('/suppliers/new')}
        >
          Add Supplier
        </Button>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search suppliers by name..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined color="action" fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSuppliers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      {searchQuery ? 'No suppliers found matching your search' : 'No suppliers found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSuppliers.map((supplier) => (
                  <TableRow key={supplier.id} hover>
                    <TableCell>
                      <Typography variant="body2">{supplier.id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{supplier.name}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={() => navigate(`/suppliers/${supplier.id}`)}
                        title="View"
                      >
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                      {(isAdmin || authService.hasPermission('suppliers_edit')) && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/suppliers/${supplier.id}/edit`)}
                          title="Edit"
                        >
                          <EditOutlined fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default SupplierList;
